import { getScheduleNowWallClock } from "../../lib/time";
import { TimezoneSelect } from "../../components/ui/timezone-select";
import { useAuth } from "../../context/AuthContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown, ChevronUp, Play, Square } from "lucide-react";
import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Dropdown } from "../../components/ui/dropdown";
import { DateTimePicker } from "../../components/ui/date-picker";
import { useToast } from "../../components/ui/toast";
import { ToggleRow } from "../../components/ui/toggle-switch";
import { MultiSelectAudioDropdown } from "../../components/ui/multi-select-audio-dropdown";
import { core } from "../../lib/api";
import { extractList } from "../home/queries";
import { listWorkflows } from "./api";
import { listContactBatches, launchContactBatch, getContactBatch, getWorkflowInfields, getWorkflowOutfields } from "./api";
import { cn } from "../../lib/utils";
import type { VoiceOption } from "../home/types";

interface ScheduleWorkflowState {
  workflowId: string;
  batchId: string;
  campaign: string;
  voiceId: string;
  did: string;
  useAlternateNumber: boolean;
  interruption: boolean;
  bgNoise: boolean;
  ambience: any[];
  backgroundAudio: any[];
  mode: "now" | "later";
  at: string;
  timezone: string;
  concurrency: number;
  /** infield name → the batch column that feeds it. "" means leave unmapped. */
  infieldMap: Record<string, string>;
  /** Agent outfield whose extracted value becomes the lead's CRM status. "" means don't change. */
  outcomeStatusOutfield: string;
}

const NOT_MAPPED = "";

/** "Phone Number" / "phone_number" / "phoneNumber" all compare equal, so an
 *  obvious column gets picked for its infield without the user doing it. */
const normalizeKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

const INIT_STATE: ScheduleWorkflowState = {
  workflowId: "",
  batchId: "",
  campaign: "",
  voiceId: "",
  did: "",
  useAlternateNumber: false,
  interruption: true,
  bgNoise: true,
  ambience: [],
  backgroundAudio: [],
  mode: "now",
  at: "",
  timezone: "Asia/Kolkata",
  concurrency: 50,
  infieldMap: {},
  outcomeStatusOutfield: "",
};

export function AutomationSchedulePage() {
  const [s, setS] = useState<ScheduleWorkflowState>(INIT_STATE);
  const patch = (next: Partial<ScheduleWorkflowState>) => setS((curr) => ({ ...curr, ...next }));
  const nav = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const keyRef = useRef(crypto.randomUUID());
  
  const [showAdditional, setShowAdditional] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audio = useRef<HTMLAudioElement | null>(null);

  // Parse workflowId from query params (only fills it in if not already set)
  useEffect(() => {
    const wId = searchParams.get("workflowId");
    const bId = searchParams.get("batchId");
    if (bId) setS(curr => curr.batchId ? curr : { ...curr, batchId: bId });
    if (wId) {
      setS((curr) => (curr.workflowId ? curr : { ...curr, workflowId: wId }));
    }
  }, [searchParams]);

  // Queries
  const workflows = useQuery({
    queryKey: ["call-workflows", "published"],
    queryFn: () => listWorkflows("published"),
  });

  const batches = useQuery({
    queryKey: ["contact-batches"],
    queryFn: listContactBatches,
  });

  // What this workflow's agents expect, and what the chosen batch actually
  // has, so the two can be lined up before launching.
  const infields = useQuery({
    queryKey: ["call-workflow-infields", s.workflowId],
    queryFn: () => getWorkflowInfields(s.workflowId),
    enabled: !!s.workflowId,
    staleTime: 300_000,
  });

  // Outfields produced by the agents in the selected workflow — one of these
  // can drive the lead's CRM status from its extracted value.
  const outfields = useQuery({
    queryKey: ["call-workflow-outfields", s.workflowId],
    queryFn: () => getWorkflowOutfields(s.workflowId),
    enabled: !!s.workflowId,
    staleTime: 300_000,
  });

  const batchSample = useQuery({
    queryKey: ["contact-batch-sample", s.batchId],
    queryFn: () => getContactBatch(s.batchId, 1, 1),
    enabled: !!s.batchId,
    staleTime: 300_000,
  });

  const batchColumns = React.useMemo(() => {
    const sample = batchSample.data?.contacts?.[0];
    if (!sample) return [] as string[];
    return ["name", "phone_number", ...Object.keys(sample.metadata ?? {})];
  }, [batchSample.data]);

  // Pre-fill the obvious pairings whenever the workflow or batch changes; the
  // user only has to touch the ones we couldn't guess.
  useEffect(() => {
    const fields = infields.data ?? [];
    if (!fields.length || !batchColumns.length) return;
    const byNormalized = new Map(batchColumns.map((column) => [normalizeKey(column), column]));
    setS((curr) => {
      const next: Record<string, string> = {};
      for (const field of fields) {
        next[field.fieldName] =
          curr.infieldMap[field.fieldName] ?? byNormalized.get(normalizeKey(field.fieldName)) ?? NOT_MAPPED;
      }
      return { ...curr, infieldMap: next };
    });
  }, [infields.data, batchColumns]);

  // A field picked for one workflow may not exist on the next one; clear it
  // rather than launching with an outfield that call can never produce.
  useEffect(() => {
    if (!outfields.isSuccess) return;
    const names = new Set((outfields.data ?? []).map((field) => field.fieldName));
    setS((curr) =>
      !curr.outcomeStatusOutfield || names.has(curr.outcomeStatusOutfield)
        ? curr
        : { ...curr, outcomeStatusOutfield: "" },
    );
  }, [outfields.data, outfields.isSuccess]);

  const voices = useQuery({
    queryKey: ["voice", "options"],
    queryFn: () => core<any>("/api/v1/voice/options").then(extractList) as Promise<VoiceOption[]>, 
    staleTime: Infinity 
  });
  
  const dids = useQuery({ 
    queryKey: ["voice", "dids"], 
    queryFn: () => core<any>("/api/v1/voice/did-options").then(extractList) as Promise<{ did_id: string; number: string }[]>, 
    staleTime: Infinity 
  });
  
  const campaigns = useQuery({ 
    queryKey: ["campaigns", "names"], 
    queryFn: () => core<any>("/api/v1/campaigns/names").then(extractList) as Promise<{ name: string }[]>, 
    staleTime: 300_000 
  });

  // 403 means the caller lacks the grant (default-off), not that the list is empty.
  // The dropdowns below already tolerate missing data; these hints say why.
  const workflowsDenied = (workflows.error as { status?: number } | null)?.status === 403;
  const campaignsDenied = (campaigns.error as { status?: number } | null)?.status === 403;
  
  const backgrounds = useQuery({ 
    queryKey: ["voice", "backgrounds"], 
    queryFn: () => core<any>("/api/v1/voice/background-options").then(extractList) as Promise<{ id: string; name: string; type: string; file_key: string }[]>, 
    staleTime: Infinity 
  });

  const defaultedThinkingSoundsRef = useRef(false);
  useEffect(() => {
    if (defaultedThinkingSoundsRef.current) return;
    if (!backgrounds.data || !s.bgNoise || (s.backgroundAudio && s.backgroundAudio.length > 0)) return;

    const thinkingSounds = (backgrounds.data ?? [])
      .filter((b: any) => b.type === "thinking_sound")
      .map((b: any) => ({ ...b, id: b.id || b.file_key }));

    if (thinkingSounds.length === 0) return;

    let defaults = thinkingSounds.filter((b: any) => {
      const name = String(b.name || "").toLowerCase();
      return name.includes("mouse") || name.includes("click") || name.includes("keyboard") || name.includes("typing");
    });

    if (defaults.length === 0) {
      defaults = thinkingSounds.filter((b: any) => b.active !== false).slice(0, 2);
    }

    if (defaults.length > 0) {
      defaultedThinkingSoundsRef.current = true;
      patch({
        backgroundAudio: defaults.map((d: any) => ({ ...d, volume: d.volume ?? 0.3 })),
      });
    }
  }, [backgrounds.data, s.bgNoise, s.backgroundAudio]);

  const preview = async () => {
    if (playing) { audio.current?.pause(); setPlaying(false); return; }
    try {
      const { url } = await core<{ url: string }>("/api/v1/voice/tts", { 
        method: "POST", 
        body: JSON.stringify({ voice_id: s.voiceId, text: "Namaste! This is how your batch will sound." }) 
      });
      audio.current = new Audio(url);
      await audio.current.play();
      setPlaying(true);
      audio.current.onended = () => setPlaying(false);
    } catch (error) {
      setPlaying(false);
      toast.danger("Voice preview failed", (error as Error).message);
    }
  };

  const launch = useMutation({
    mutationFn: () => {
      let scheduleDate = "";
      let scheduleTime = "";
      let scheduleTimezone = s.timezone || "Asia/Kolkata";

      if (s.mode === "now") {
        const wallClock = getScheduleNowWallClock(scheduleTimezone, 2);
        scheduleDate = wallClock.scheduleDate;
        scheduleTime = wallClock.scheduleTime;
      } else {
        [scheduleDate, scheduleTime] = s.at.split("T");
      }

      // Only the pairs the user actually set — an unmapped infield must stay
      // absent so the agent falls back to its own default.
      const infield_mapping = Object.fromEntries(
        Object.entries(s.infieldMap).filter(([, column]) => !!column),
      );

      return launchContactBatch(s.batchId, {
        idempotency_key: keyRef.current,
        ...(Object.keys(infield_mapping).length ? { infield_mapping } : {}),
        ...(s.outcomeStatusOutfield ? { outcome_status_outfield: s.outcomeStatusOutfield } : {}),
        schedule: {
          workflow_id: s.workflowId,
          campaign: s.campaign.trim() || "NA",
          voice: s.voiceId,
          did_number_id: s.did,
          interruption: s.interruption,
          background_noise: s.bgNoise,
          ambience: s.bgNoise ? s.ambience : [],
          background_audio: s.bgNoise ? s.backgroundAudio : [],
          use_alternate_number: s.useAlternateNumber,
          schedule_date: scheduleDate,
          schedule_time: scheduleTime,
          schedule_timezone: scheduleTimezone,
          batch_call_concurrency: s.concurrency,
        }
      });
    },
    onSuccess: (data) => {
      data.idempotent ? toast.info("Batch already created") : toast.success("Workflow batch launched", "Track it in Active Batches.");
      nav("/batch");
    },
    onError: (error) => toast.danger("Launch failed", (error as Error).message),
  });

  const isFormValid = !!s.workflowId && !!s.batchId && !!s.voiceId && !!s.did && (s.mode === "now" || (!!s.at && new Date(s.at) > new Date()));
  const past = s.mode === "later" && !!s.at && new Date(s.at) <= new Date();

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-4 sm:p-6">
      <div className="mb-6 flex items-start gap-3.5">
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) {
              nav(-1);
            } else {
              nav("/home/automation");
            }
          }}
          aria-label="Back to workflows"
          title="Back to workflows"
          className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl border border-line bg-elevated text-ink-2 shadow-2xs hover:bg-soft hover:text-ink transition-colors shrink-0"
        >
          <ArrowLeft className="h-4.5 w-4.5" />
        </button>
        <div>
          <h1 className="text-sm font-semibold text-ink">Schedule Workflow</h1>

        </div>
      </div>

      <div className="space-y-6">
        {/* Section 1: Workflow & Batch Selection */}
        <div className="rounded-2xl border border-line bg-elevated p-4 sm:p-6 shadow-sm space-y-4">
          <h2 className="mb-4 text-base font-bold uppercase tracking-wider text-ink-3">1. Sequence & Contacts</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Workflow</span>
              <Dropdown 
                ariaLabel="Workflow" 
                value={s.workflowId} 
                options={[
                  { value: "", label: "Select workflow…" },
                  ...(workflows.data?.filter(w => !!w.id).map(w => ({ value: w.id, label: w.name || w.id })) ?? [])
                ]} 
                onChange={(v) => patch({ workflowId: v })} 
              />
              {workflowsDenied && (
                <p className="mt-1 text-[11px] text-ink-3">No access to workflows — ask your organization owner.</p>
              )}
            </div>
            <div>
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Contact Batch</span>
              <Dropdown 
                ariaLabel="Contact Batch" 
                value={s.batchId} 
                options={[
                  { value: "", label: "Select batch…" },
                  ...(batches.data?.filter((b: any) => b.status === "ready" && !!b.batch_id).map((b: any) => ({ value: b.batch_id, label: `${b.name} (${b.valid_contacts} contacts)` })) ?? [])
                ]} 
                onChange={(v) => patch({ batchId: v })} 
              />
            </div>
          </div>
        </div>

        {/* Section 1b: Infield mapping — only once both sides are known. */}
        {!!s.workflowId && !!s.batchId && (infields.isLoading || (infields.data?.length ?? 0) > 0) && (
          <div className="rounded-2xl border border-line bg-elevated p-4 sm:p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-base font-bold uppercase tracking-wider text-ink-3">Infield Mapping</h2>
            </div>

            {infields.isLoading ? (
              <p className="text-sm text-ink-3">Loading infields…</p>
            ) : (
              <div className="space-y-3">
                {(infields.data ?? []).map((field) => (
                  <div key={field.fieldName} className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:items-center">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-xs font-semibold text-ink">{field.fieldName}</p>
                      <p className="truncate text-[11px] text-ink-3">
                        {field.description || field.fieldType}
                        {field.agents.length > 0 && ` · ${field.agents.join(", ")}`}
                      </p>
                    </div>
                    <Dropdown
                      ariaLabel={`Batch column for ${field.fieldName}`}
                      value={s.infieldMap[field.fieldName] ?? NOT_MAPPED}
                      options={[
                        { value: NOT_MAPPED, label: "Not mapped" },
                        ...batchColumns.map((column) => ({ value: column, label: column })),
                      ]}
                      onChange={(v) => patch({ infieldMap: { ...s.infieldMap, [field.fieldName]: v } })}
                    />
                  </div>
                ))}
                {batchColumns.length === 0 && !batchSample.isLoading && (
                  <p className="text-xs text-ink-3">
                    This batch has no columns to map from — add them on the batch's contacts page.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Section 1c: Outcome -> CRM status — auto-updates a lead's CRM
            status from one extracted outfield value (e.g. "Booked: false"),
            instead of an agent setting it by hand after every call. Only
            meaningful once there's a batch to launch against; doesn't depend
            on infields. */}
        {!!s.workflowId && !!s.batchId && (
          <div className="rounded-2xl border border-line bg-elevated p-4 sm:p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-base font-bold uppercase tracking-wider text-ink-3">Outcome → CRM Status</h2>
              <p className="mt-1 text-xs text-ink-3">
                Pick an outfield extracted from the call. Each contact's CRM lead status becomes
                "Field: value" from its latest call — e.g. "Booked: false". Leave as "Don't change" to skip.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:items-center">
              <p className="truncate text-xs font-semibold text-ink">CRM status from outfield</p>
              <Dropdown
                ariaLabel="CRM status outfield"
                value={s.outcomeStatusOutfield || NOT_MAPPED}
                options={[
                  { value: NOT_MAPPED, label: "Don't change" },
                  ...(outfields.data ?? []).map((field) => ({
                    value: field.fieldName,
                    label: field.fieldName,
                  })),
                ]}
                onChange={(v) => patch({ outcomeStatusOutfield: v })}
              />
            </div>
            {outfields.isSuccess && (outfields.data ?? []).length === 0 && (
              <p className="text-xs text-ink-3">
                This workflow's agents don't define any outfields yet.
              </p>
            )}
          </div>
        )}

        {/* Section 2: Telecom Configuration */}
        <div className="rounded-2xl border border-line bg-elevated p-4 sm:p-6 shadow-sm space-y-4">
          <h2 className="mb-4 text-base font-bold uppercase tracking-wider text-ink-3">2. Voice & Telecom</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Voice</span>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Dropdown 
                    ariaLabel="Voice" 
                    value={s.voiceId} 
                    options={[
                      { value: "", label: "Select voice…" },
                      ...(voices.data?.map((v: any) => ({ 
                        value: v.local_voice_name || v.voice_id || v.id || "", 
                        label: v.name || v.local_voice_name || v.voice_id || "" 
                      })).filter((o: any) => !!o.value) ?? [])
                    ]} 
                    onChange={(v) => patch({ voiceId: v })} 
                  />
                </div>
                <button onClick={preview} disabled={!s.voiceId} aria-label="Preview voice" className="h-10 rounded-lg bg-soft px-3 text-ink hover:bg-line disabled:opacity-40">
                  {playing ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Caller ID</span>
              <Dropdown 
                ariaLabel="Caller ID" 
                value={s.did} 
                options={[
                  { value: "", label: "Select caller ID…" },
                  ...(dids.data?.map((d: any) => { 
                    if (typeof d === 'string') return { value: d, label: d };
                    return { value: d.did_id || d.id || d.number || "", label: d.number || d.name || d.did_id || "" };
                  }).filter((o: any) => !!o.value) ?? [])
                ]} 
                onChange={(v) => patch({ did: v })} 
              />
            </div>
          </div>

          <div>
            <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Campaign</span>
            <div className="flex flex-col sm:flex-row gap-2">
              <input 
                value={s.campaign} 
                onChange={(e) => patch({ campaign: e.target.value })} 
                placeholder="Type campaign name..."
                onBlur={async () => { if (s.campaign.trim()) { await core(`/api/v1/campaigns/names?name=${encodeURIComponent(s.campaign.trim())}`, { method: "POST" }).catch(() => {}); } }}
                onKeyDown={async (e) => { if (e.key === "Enter" && s.campaign.trim()) { e.preventDefault(); await core(`/api/v1/campaigns/names?name=${encodeURIComponent(s.campaign.trim())}`, { method: "POST" }).catch(() => {}); } }}
                className="flex-1 rounded-lg border border-line px-3 py-2 text-sm focus:border-brand focus:outline-none" 
              />
              <div className="w-full sm:w-48">
                <Dropdown 
                  ariaLabel="Previous campaigns" 
                  value={s.campaign} 
                  options={[
                    { value: "", label: "Select previous..." }, 
                    ...(campaigns.data?.map((c: any) => {
                      if (typeof c === 'string') return { value: c, label: c };
                      return { value: c.name || c.id || "", label: c.name || c.id || "" };
                    }).filter((o: any) => !!o.value) ?? [])
                  ]} 
                  onChange={(v) => patch({ campaign: v })} 
                />
                {campaignsDenied && (
                  <p className="mt-1 text-[11px] text-ink-3">Previous campaigns unavailable — ask your organization owner for access.</p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-line">
            <button onClick={() => setShowAdditional(x => !x)} className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-ink hover:bg-soft">
              Additional Settings
              {showAdditional ? <ChevronUp className="h-4 w-4 text-ink-3" /> : <ChevronDown className="h-4 w-4 text-ink-3" />}
            </button>
            {showAdditional && (
              <div className="border-t border-line p-4 space-y-3">
                <ToggleRow on={s.useAlternateNumber} onClick={() => patch({ useAlternateNumber: !s.useAlternateNumber })} label="Retry with Alternate Number" />
                <ToggleRow on={s.interruption} onClick={() => patch({ interruption: !s.interruption })} label="Allow user interruption" />
                <ToggleRow on={s.bgNoise} onClick={() => patch({ bgNoise: !s.bgNoise })} label="Background noise" sub="Unlocks ambience + thinking sounds" />
                
                <div className={cn("grid grid-cols-1 sm:grid-cols-2 gap-3", !s.bgNoise && "pointer-events-none opacity-40")}>
                  <div>
                    <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Ambience</span>
                    <MultiSelectAudioDropdown
                      label="Ambience"
                      placeholder="Select ambience..."
                      options={(backgrounds.data ?? []).filter((b: any) => b.type === "ambient_sound").map((b: any) => ({ ...b, id: b.id || b.file_key }))}
                      value={s.ambience}
                      onChange={(v) => patch({ ambience: v })}
                    />
                  </div>
                  <div>
                    <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Thinking sound</span>
                    <MultiSelectAudioDropdown
                      label="Thinking Sound"
                      placeholder="Select thinking sound..."
                      options={(backgrounds.data ?? []).filter((b: any) => b.type === "thinking_sound").map((b: any) => ({ ...b, id: b.id || b.file_key }))}
                      value={s.backgroundAudio}
                      onChange={(v) => patch({ backgroundAudio: v })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section 3: Scheduling */}
        <div className="rounded-2xl border border-line bg-elevated p-4 sm:p-6 shadow-sm space-y-4">
          <h2 className="mb-4 text-base font-bold uppercase tracking-wider text-ink-3">3. Timing</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {([["now", "Run Now", "Start dialing as soon as created"], ["later", "Target Time", "Pick date & time"]] as const).map(([id, t, sub]) => (
              <button key={id} onClick={() => patch({ mode: id as "now" | "later" })}
                className={cn("rounded-xl border p-4 text-left", s.mode === id ? "border-brand bg-brand-soft/30" : "border-line hover:bg-soft")}>
                <p className="font-bold text-ink">{t}</p><p className="text-xs text-ink-3">{sub}</p>
              </button>
            ))}
          </div>

          {s.mode === "later" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Dial at</span>
                <DateTimePicker
                  value={s.at}
                  onChange={(val) => patch({ at: val })}
                  placeholder="Pick target date & time"
                  minDate={new Date().toISOString().slice(0, 10)}
                  className={cn("w-full", past && "[&>button]:border-danger/50")}
                />
                {past && <span className="mt-1 block text-xs text-danger">Must be in the future.</span>}
              </label>
              <div>
                <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Timezone</span>
                <TimezoneSelect ariaLabel="Timezone" value={s.timezone} onChange={(v) => patch({ timezone: v })} />
              </div>
            </div>
          )}

          <div>
            <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Concurrency</span>
            <input 
              type="number" 
              value={s.concurrency} 
              onChange={(e) => patch({ concurrency: parseInt(e.target.value, 10) || 1 })} 
              min={1}
              className="w-full max-w-[200px] rounded-lg border border-line px-3 py-2 text-sm focus:border-brand focus:outline-none" 
            />
            <p className="mt-1 text-[10px] text-ink-3">How many calls to run in parallel.</p>
          </div>
        </div>

        {/* Launch */}
        <button 
          onClick={() => launch.mutate()} 
          disabled={!isFormValid || launch.isPending}
          className="w-full rounded-lg bg-ink py-3 font-bold text-canvas hover:opacity-90 disabled:cursor-not-allowed disabled:bg-line disabled:text-ink-3 transition-opacity"
        >
          {launch.isPending ? "Launching..." : "Launch Sequence"}
        </button>
      </div>
    </div>
  );
}
